/**
 * 作品上映排期页（DEV-15088）CMS 与观众页共用的常量和纯函数
 */

// 配色（与 demo schedule.html 的 9 种类型配色一致）；颜色变量在 themes.scss 里定义
export const STYLE_OPTIONS = [
  { value: 'classic', label: '一般', desc: '一般・定番' },
  { value: 'kokubo', label: '時代劇', desc: '時代劇・歴史映画' },
  { value: 'noir', label: 'サスペンス', desc: 'サスペンス・ノワール' },
  { value: 'midnight', label: 'SF・ミステリー', desc: 'SF・ミステリー' },
  { value: 'forest', label: 'ドラマ', desc: 'ヒューマンドラマ・自然' },
  { value: 'sunset', label: 'ロマンス', desc: 'ロマンス・恋愛' },
  { value: 'horror', label: 'ホラー', desc: 'ホラー・スリラー' },
  { value: 'comedy', label: 'コメディ', desc: 'コメディ・ライト' },
  { value: 'fantasy', label: 'ファンタジー', desc: 'ファンタジー・アニメ' },
];
export const DEFAULT_STYLE = 'classic';

// 显示方式：compact = 九宫格（按时间段分 8 列），list = 列表卡片
export const LAYOUT_OPTIONS = [
  { value: 'compact', label: 'タイムテーブル（グリッド）' },
  { value: 'list', label: 'リスト' },
];
export const DEFAULT_LAYOUT = 'compact';

// 未知值回退到默认值，避免后端返回脏数据时页面没有配色
export const normalizeStyle = (v) => (STYLE_OPTIONS.some((o) => o.value === v) ? v : DEFAULT_STYLE);
export const normalizeLayout = (v) => (LAYOUT_OPTIONS.some((o) => o.value === v) ? v : DEFAULT_LAYOUT);

// 观众页公开链接
export const buildPublicUrl = (pageKey) => `${window.location.origin}/public/schedule/${pageKey}`;

/** EP 时间 "yyyy/MM/dd HH:mm:ss" → "HH:mm"；格式不符返回 null */
export function clockOf(datetime) {
  const match = /(\d{2}):(\d{2}):\d{2}$/.exec(datetime ?? '');
  return match ? `${match[1]}:${match[2]}` : null;
}

/**
 * "HH:mm" 加上分钟数；过了午夜按日本影院惯例显示为 25:10，避免结束时间看起来早于开始时间
 */
export function addMinutes(clock, minutes) {
  const [h, m] = clock.split(':').map(Number);
  const total = h * 60 + m + minutes;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * 结束时间："HH:mm"
 * showEndTime 可能为 null（实测东京+大阪一天有 26 个），用开始时间 + 片长补算；片长未知（0）时返回 null
 * 跨日的结束时间（如 01:10）按开始日换算成 25:10
 */
export function endClockOf(showing, duration) {
  const start = clockOf(showing.showStartTime);
  if (!start) return null;
  const end = clockOf(showing.showEndTime);
  if (end) {
    const startDay = (showing.showStartTime || '').slice(0, 10);
    const endDay = (showing.showEndTime || '').slice(0, 10);
    // 结束日晚于开始日：小时数 +24
    if (startDay && endDay && endDay > startDay) {
      const [h, m] = end.split(':');
      return `${String(Number(h) + 24).padStart(2, '0')}:${m}`;
    }
    return end;
  }
  return duration > 0 ? addMinutes(start, duration) : null;
}

/**
 * 九宫格的时间轴（与 ep-web-new booking / App 一致）：按开始小时分 8 列
 * 0–5 | 6–12 | 13–14 | 15–16 | 17–18 | 19–20 | 21 | 22+
 */
export const TRACK_COLUMNS = 8;
export function trackColumn(clock) {
  const hour = Number(clock.slice(0, 2));
  if (hour <= 5) return 0;
  if (hour <= 12) return 1;
  if (hour <= 20) return 2 + Math.floor((hour - 13) / 2);
  return hour === 21 ? 6 : 7;
}

/** 把一家影院的场次放进九宫格：按开始时间排序，同一列已有场次时往下叠 */
export function placeOnTrack(showings) {
  const nextRow = {};
  return [...showings]
    .sort((a, b) => a.clock.localeCompare(b.clock))
    .map((showing) => {
      const column = trackColumn(showing.clock);
      const row = nextRow[column] ?? 0;
      nextRow[column] = row + 1;
      return { ...showing, column, row };
    });
}

const DOW = ['日', '月', '火', '水', '木', '金', '土'];

/** 日本时区的今天 "yyyy/MM/dd"（与访问者所在时区无关） */
export function todayInJapan(now = new Date()) {
  const parts = new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(now);
  const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}/${get('month')}/${get('day')}`;
}

/** 日期条的标签：本日 / 明日 / M月D日，以及星期 */
export function dateLabel(date, today) {
  const [y, m, d] = date.split('/').map(Number);
  const [ty, tm, td] = today.split('/').map(Number);
  const target = Date.UTC(y, m - 1, d);
  const offset = Math.round((target - Date.UTC(ty, tm - 1, td)) / 86400000);
  const dowIndex = new Date(target).getUTCDay();
  return {
    dow: DOW[dowIndex],
    // 星期六 / 日用于着色
    dowIndex,
    label: offset === 0 ? '本日' : offset === 1 ? '明日' : `${m}/${d}`,
  };
}

/** 两点间距离（km，haversine） */
export function distanceKm(from, lat, lon) {
  const la = Number(lat);
  const lo = Number(lon);
  if (!from || !lat || !lon || !Number.isFinite(la) || !Number.isFinite(lo)) return Infinity;
  const rad = (deg) => (deg * Math.PI) / 180;
  const a = Math.sin(rad(la - from.lat) / 2) ** 2
    + Math.cos(rad(from.lat)) * Math.cos(rad(la)) * Math.sin(rad(lo - from.lon) / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(a));
}

/** 距离显示：10km 以内两位小数 */
export const formatKm = (km) => (Number.isFinite(km) ? `${km < 10 ? km.toFixed(2) : km.toFixed(1)} km` : '');

/** 公开日 "yyyy/MM/dd" → "yyyy年M月D日(曜)公開" */
export function formatOpenDate(openDate) {
  const match = /^(\d{4})\/(\d{2})\/(\d{2})/.exec(openDate ?? '');
  if (!match) return '';
  const [, y, m, d] = match;
  const dow = DOW[new Date(Date.UTC(+y, +m - 1, +d)).getUTCDay()];
  return `${y}年${Number(m)}月${Number(d)}日(${dow})公開`;
}
